import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  styled,
  Tooltip,
} from '@mui/material';

import {
  IMasterNameRelationBehavior,
  KaseMasterNameInvolvementLabels,
  MasterNameInvolvement,
} from '@/models';
import { MasterNameType } from '@/models/master.names';
import { ConfirmTypes, useToastStore } from '@/store';

import { disableOwnerInvolvementOption } from '../registeredOwnerVehicle';

const InvolvementChip = styled(Chip)(({ theme }) => ({
  borderRadius: '4px',
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  height: '16px',
  fontWeight: 500,
  fontSize: '0.7rem',
  flexShrink: 0,
  '& .MuiChip-label': {
    padding: '0 5px',
    lineHeight: 1,
  },
}));

const CHIP_WIDTH = 28;
const OVERFLOW_CHIP_WIDTH = 24;
const CHIP_GAP = 4;
const SELECT_PADDING = 32;
const SELECT_WIDTH = '100%';
const SELECT_HEIGHT = 20;

export interface InvolvementInlineSelectProps {
  nameId: string;
  currentInvolvement: MasterNameInvolvement[];
  currentBehavior?: IMasterNameRelationBehavior;
  masterNameType: MasterNameType;
  isReadOnly?: boolean;
  disableOwnerRemoval?: boolean;
  onUpdate: (
    nameId: string,
    involvements: MasterNameInvolvement[],
    behavior: IMasterNameRelationBehavior,
  ) => Promise<void>;
}

type InvolvementOption = {
  value: MasterNameInvolvement;
  label: string;
  disabled?: boolean;
};

const getFullLabel = (code: string): string => {
  const label =
    KaseMasterNameInvolvementLabels[code as MasterNameInvolvement] || code;
  return `${code} - ${label}`;
};

const calculateVisibleChips = (
  containerWidth: number,
  totalChips: number,
): number => {
  if (totalChips === 0) return 0;

  const availableWidth = containerWidth - SELECT_PADDING;

  if (totalChips === 1) return 1;

  let maxChips = Math.floor(
    (availableWidth + CHIP_GAP) / (CHIP_WIDTH + CHIP_GAP),
  );

  if (maxChips >= totalChips) return totalChips;

  const widthWithOverflow = availableWidth - OVERFLOW_CHIP_WIDTH - CHIP_GAP;
  maxChips = Math.floor(
    (widthWithOverflow + CHIP_GAP) / (CHIP_WIDTH + CHIP_GAP),
  );

  return Math.max(1, maxChips);
};

const InvolvementInlineSelect = ({
  nameId,
  currentInvolvement,
  currentBehavior,
  masterNameType,
  isReadOnly = false,
  disableOwnerRemoval = false,
  onUpdate,
}: InvolvementInlineSelectProps) => {
  const { updateToast } = useToastStore();
  const [isLoading, setIsLoading] = useState(false);
  const [localValue, setLocalValue] =
    useState<MasterNameInvolvement[]>(currentInvolvement);
  const [containerWidth, setContainerWidth] = useState(200);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalValue(currentInvolvement);
  }, [currentInvolvement]);

  useLayoutEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const maxVisibleChips = useMemo(
    () => calculateVisibleChips(containerWidth, localValue.length),
    [containerWidth, localValue.length],
  );

  const options = useMemo(() => {
    // if (masterNameType === MasterNameType.ORGANIZATION) {
    //   return [
    //     {
    //       value: MasterNameInvolvement.VICTIM,
    //       label: getFullLabel(MasterNameInvolvement.VICTIM),
    //     },
    //   ] as InvolvementOption[];
    // }

    return disableOwnerInvolvementOption(
      Object.values(MasterNameInvolvement).map((value) => ({
        value,
        label: getFullLabel(value),
      })) as InvolvementOption[],
      disableOwnerRemoval,
    );
  }, [disableOwnerRemoval, masterNameType]);

  const getOverflowTooltip = useCallback((codes: string[]) => {
    return codes.map((code) => getFullLabel(code)).join('\n');
  }, []);

  const handleChange = useCallback(
    (event: SelectChangeEvent<MasterNameInvolvement[]>) => {
      const newValue =
        typeof event.target.value === 'string'
          ? event.target.value.split(',')
          : event.target.value;
      setLocalValue(newValue as MasterNameInvolvement[]);
    },
    [],
  );

  const handleClose = useCallback(async () => {
    const hasChanged =
      localValue.length !== currentInvolvement.length ||
      localValue.some((v) => !currentInvolvement.includes(v));

    if (!hasChanged) return;

    if (localValue.length === 0) {
      updateToast({
        open: true,
        message: 'Please add at least one involvement for this person.',
        type: ConfirmTypes.WARNING,
      });
      setLocalValue(currentInvolvement);
      return;
    }

    setIsLoading(true);
    try {
      const behavior = currentBehavior || 'N/A';
      await onUpdate(nameId, localValue, behavior);
    } catch (error: any) {
      setLocalValue(currentInvolvement);
      updateToast({
        open: true,
        message: error?.message || 'Failed to update involvement',
        type: ConfirmTypes.ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    localValue,
    currentInvolvement,
    currentBehavior,
    nameId,
    onUpdate,
    updateToast,
  ]);

  const renderChips = useCallback(
    (selected: string[]) => {
      if (!selected.length) {
        return (
          <Box sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            Select...
          </Box>
        );
      }

      const visibleChips = selected.slice(0, maxVisibleChips);
      const overflowChips = selected.slice(maxVisibleChips);
      const overflowCount = overflowChips.length;

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: `${CHIP_GAP}px`,
            height: '100%',
            maxHeight: SELECT_HEIGHT,
            overflow: 'hidden',
          }}
        >
          {visibleChips.map((code) => (
            <Tooltip key={code} title={getFullLabel(code)} arrow>
              <Box
                component="span"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <InvolvementChip label={code} size="small" />
              </Box>
            </Tooltip>
          ))}
          {overflowCount > 0 && (
            <Tooltip
              title={
                <Box sx={{ whiteSpace: 'pre-line' }}>
                  {getOverflowTooltip(overflowChips)}
                </Box>
              }
              arrow
            >
              <Box
                component="span"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <InvolvementChip label={`+${overflowCount}`} size="small" />
              </Box>
            </Tooltip>
          )}
        </Box>
      );
    },
    [maxVisibleChips, getOverflowTooltip],
  );

  if (isReadOnly) {
    if (!currentInvolvement.length) return <>-</>;

    const visibleChips = currentInvolvement.slice(0, maxVisibleChips);
    const overflowChips = currentInvolvement.slice(maxVisibleChips);
    const overflowCount = overflowChips.length;

    return (
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: `${CHIP_GAP}px`,
          width: '100%',
        }}
      >
        {visibleChips.map((code) => (
          <Tooltip key={code} title={getFullLabel(code)} arrow>
            <Box
              component="span"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <InvolvementChip label={code} size="small" />
            </Box>
          </Tooltip>
        ))}
        {overflowCount > 0 && (
          <Tooltip
            title={
              <Box sx={{ whiteSpace: 'pre-line' }}>
                {getOverflowTooltip(overflowChips)}
              </Box>
            }
            arrow
          >
            <Box
              component="span"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <InvolvementChip label={`+${overflowCount}`} size="small" />
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: SELECT_WIDTH,
          height: '100%',
        }}
      >
        <CircularProgress size={14} color="error" />
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={{ width: SELECT_WIDTH }}>
      <Select
        multiple
        value={localValue}
        onChange={handleChange}
        onClose={handleClose}
        size="small"
        displayEmpty
        renderValue={renderChips}
        sx={{
          width: '100%',
          height: SELECT_HEIGHT,
          minHeight: 'unset !important',
          '& .MuiInputBase-root': {
            minHeight: 'unset !important',
          },
          '& .MuiSelect-select': {
            py: 0,
            px: 1,
            display: 'flex',
            alignItems: 'center',
            minHeight: 'unset !important',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'divider',
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 300,
              '& .MuiList-root': {
                py: 0.5,
              },
            },
          },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            sx={{ py: 0.25, px: 1, minHeight: 'unset' }}
          >
            <Checkbox
              checked={localValue.includes(option.value)}
              disabled={option.disabled}
              size="small"
              sx={{ p: 0.5 }}
            />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

export default InvolvementInlineSelect;
